import { useMemo } from "react";

import {
    StrategyRewards,
    useActivePortfolioRewards,
    usePortfolioRewards,
    useUserLendingVaultPositions,
    LedgerReward,
    VaultReward,
    EarnVaultReward,
    LoopscalePointsAction,
    VaultMetadataTag,
    UserExternalRewardsInfoExpanded,
    RewardMint
} from "@bridgesplit/abf-react";
import { TIME } from "@bridgesplit/utils";
import { useLendingPositionPagination } from "app/api";

import { SummarizedActivePortfolioRewards } from "./type";

interface ActivePortfolioRewardsResponse {
    data: SummarizedActivePortfolioRewards | undefined;
    isLoading: boolean;
}
export function useActivePortfolioRewardsSummary(): ActivePortfolioRewardsResponse {
    const { data: portfolioRewards, isLoading: isLoadingPortfolioRewards } = useActivePortfolioRewards();

    // Use useMemo to ensure this calculation only runs when dependencies change
    const data = useMemo(() => {
        if (!portfolioRewards) return undefined;

        return {
            activeStrategyDailyLendPoints: summarizeDailyActiveLendPoints(portfolioRewards.strategies ?? {}),
            activeBorrowDailyPoints: summarizeDailyActiveBorrowPoints(portfolioRewards.loans ?? {}),
            activeLendVaultDailyPoints: summarizeDailyActiveLendVaultPoints(portfolioRewards.vaultStakes ?? {}),
            activeEarnVaultDailyPoints: summarizeDailyActiveEarnVaultPoints(portfolioRewards.earnVaults ?? {}),
            activeLoopDailyPoints: summarizeDailyActiveBorrowPoints(portfolioRewards.loops ?? {})
        };
    }, [portfolioRewards]);

    return {
        data,
        isLoading: isLoadingPortfolioRewards
    };
}

function summarizeDailyActiveLendPoints(strategies: Record<string, StrategyRewards>): number | undefined {
    let totalPoints = 0;
    let hasActiveStrategy = false;

    for (const strategy of Object.values(strategies)) {
        const points = calculateActiveStrategyDailyPoints(strategy);
        if (points !== undefined) {
            totalPoints += points;
            hasActiveStrategy = true;
        }
    }

    return hasActiveStrategy ? totalPoints : undefined;
}

function summarizeDailyActiveBorrowPoints(loans: Record<string, LedgerReward[]>): number | undefined {
    let totalPoints = 0;
    let hasActiveLoan = false;

    for (const loan of Object.values(loans)) {
        const points = calculateActiveLoanDailyPoints(loan, true);
        if (points !== undefined) {
            totalPoints += points;
            hasActiveLoan = true;
        }
    }

    return hasActiveLoan ? totalPoints : undefined;
}

function summarizeDailyActiveLendVaultPoints(vaultStakes: Record<string, VaultReward>): number | undefined {
    let totalPoints = 0;
    let hasActiveStake = false;

    for (const stake of Object.values(vaultStakes)) {
        const points = calculateLendVaultDailyPoints(stake);
        if (points !== undefined) {
            totalPoints += points;
            hasActiveStake = true;
        }
    }

    return hasActiveStake ? totalPoints : undefined;
}

function summarizeDailyActiveEarnVaultPoints(earnVaults: Record<string, EarnVaultReward>): number | undefined {
    let totalPoints = 0;
    let hasActiveEarnVault = false;

    for (const earnVault of Object.values(earnVaults)) {
        const points = calculateEarnVaultDailyPoints(earnVault);
        if (points !== undefined) {
            totalPoints += points;
            hasActiveEarnVault = true;
        }
    }

    return hasActiveEarnVault ? totalPoints : undefined;
}

export function getStrategyIdleCapMultiplier(strategyReward: StrategyRewards | undefined): number {
    return strategyReward?.externalYieldRewards?.baseReward?.multiplier ?? 0;
}

export function getStrategyLendMultiplier(strategyReward: StrategyRewards | undefined): number {
    if (!strategyReward?.ledgerRewards?.length) return 0;

    const activeLenderRewards = strategyReward.ledgerRewards.filter(
        (reward) => reward.baseReward.isLenderReward && reward.baseReward.isActive
    );

    const multiplierMax = Math.max(...activeLenderRewards.map((reward) => reward.baseReward.multiplier));

    return multiplierMax;
}

export function getStrategyLoopscaleActions(strategyReward: StrategyRewards | undefined): LoopscalePointsAction[] {
    if (!strategyReward) return [];

    const actions: LoopscalePointsAction[] = [];

    for (const reward of strategyReward.ledgerRewards ?? []) {
        actions.push(reward.baseReward.reward as LoopscalePointsAction);
    }

    actions.push(strategyReward.externalYieldRewards?.baseReward?.reward as LoopscalePointsAction);

    return actions;
}

export function calculateActiveStrategyDailyPoints(strategyReward: StrategyRewards | undefined): number | undefined {
    if (!strategyReward) return undefined;

    const ledgerPoints =
        strategyReward.ledgerRewards
            ?.filter((reward) => reward.baseReward.isLenderReward)
            .reduce((sum, reward) => {
                return sum + (reward.baseReward?.pointsPerSecond || 0);
            }, 0) || 0;

    const externalPoints = strategyReward.externalYieldRewards?.baseReward?.pointsPerSecond || 0;

    const totalPoints = ledgerPoints + externalPoints;
    const dailyPoints = totalPoints * TIME.DAY;

    return dailyPoints > 0 ? dailyPoints : undefined;
}

export function calculateActiveLoanDailyPoints(
    loanReward: LedgerReward[] | undefined,
    isBorrower: boolean
): number | undefined {
    if (!loanReward || !Array.isArray(loanReward)) return undefined;

    const ledgerPoints = loanReward.reduce((sum, reward) => {
        // Skip rewards that don't match the user type
        const isLenderReward = reward.baseReward.isLenderReward && !isBorrower;
        const isBorrowerReward = !reward.baseReward.isLenderReward && isBorrower;

        if (!isLenderReward && !isBorrowerReward) {
            return sum;
        }

        return sum + reward.baseReward.pointsPerSecond;
    }, 0);

    // Convert to daily points
    return ledgerPoints * TIME.DAY;
}

export function calculateLendVaultDailyPoints(stakeReward: VaultReward | undefined): number | undefined {
    if (!stakeReward || !stakeReward.baseReward) return undefined;

    const ledgerPoints = stakeReward.baseReward.pointsPerSecond || 0;

    const totalPoints = ledgerPoints;
    const dailyPoints = totalPoints * TIME.DAY;

    return dailyPoints > 0 ? dailyPoints : undefined;
}

export function calculateEarnVaultDailyPoints(earnVaultReward: EarnVaultReward | undefined): number | undefined {
    if (!earnVaultReward || !earnVaultReward.baseReward) return undefined;

    const dailyPoints = (earnVaultReward.baseReward.pointsPerSecond || 0) * TIME.DAY;

    return dailyPoints > 0 ? dailyPoints : undefined;
}

export function useGenesisRewardsPoints() {
    const pagination = useLendingPositionPagination();

    const { data: vaultPositions, isLoading: isLoadingVaultPositions } = useUserLendingVaultPositions(
        pagination.params
    );

    const vaultPositionsFromGenesisVaults = vaultPositions?.flatMap((pos) =>
        pos.vaultExpanded?.vaultMetadata?.isGenesisVault ? [pos.address] : []
    );

    const vaultPositionsFromGenesisVaultsSet = new Set(vaultPositionsFromGenesisVaults);

    const { data: portfolioRewardsForGenesisVaults, isLoading: isLoadingPortfolioRewards } = usePortfolioRewards({});

    const vaultStakesFromGenesisVaults = Object.fromEntries(
        Object.entries(portfolioRewardsForGenesisVaults?.vaultStakes ?? {}).filter(([address]) =>
            vaultPositionsFromGenesisVaultsSet.has(address)
        )
    );

    const genesisVaultPoints = Object.values(vaultStakesFromGenesisVaults).reduce((sum, stake) => {
        const isGenesisVault = stake.vaultMetadata?.tags?.includes(VaultMetadataTag.Genesis) ?? false;
        return isGenesisVault ? sum + stake.baseReward.pointsPerSecond : sum;
    }, 0);

    return {
        totalPoints: genesisVaultPoints,
        isLoading: isLoadingPortfolioRewards || isLoadingVaultPositions
    };
}

export function hasClaimableExternalRewards(
    externalRewards: Record<RewardMint, UserExternalRewardsInfoExpanded> | undefined
): boolean {
    if (!externalRewards) return false;
    return Object.values(externalRewards).some((r) => (r.pendingRewards ?? 0) > 0);
}
